import React, { useState, useEffect } from 'react';
import useTranslation from 'Hooks/useTranslation';
import { Checkbox, FormControlLabel, MenuItem, TextField } from '@mui/material';
import FormValidations from 'Utils/FormValidations';
import { MoreVert, Notes } from '@mui/icons-material';
import Alert from 'Components/Alert';
import Modal from 'Components/Modal';
import Options from 'Components/Options';
import { FACE_TO_FACE } from 'Constants/General';
import TextInput from 'Components/FormComponents/TextInput';
import { NO_EMPTY_NO_EMPTY_SPACES } from 'Constants/Regex';
import { colors } from 'Constants/Colors';
import ModalInformation from '../../Components/ModalInformation';
import useModal from '../../Hooks/useModal';
import useLesson from '../../Hooks/useLesson';
import useSession from '../../Hooks/useSession';
import useUsers from '../../Hooks/useUsers';
import Title from '../../Components/Title';
import PrivateLessonForm from '../../Components/PrivateLesson';
import {
  nameStyle,
  FormContainer,
  DateContainer,
  DateOptions,
  dateStyle,
  timeStyle,
  IconTextContainer,
  iconTextArea,
  streetStyle,
  AddressLine,
  zipCodeStyle,
  AddressContainer,
} from './style';

function LessonFace({
  addLessonSession,
  module,
  updateLesson,
  currentPositionLesson,
  deleteLesson,
  updateLessonSessionData,
  courseId,
  alert,
  setAlert,
}) {
  const lesson = module.lessons[currentPositionLesson];
  const splitAddress = lesson?.address?.split('-') || '';

  const [formData, setFormData] = useState({
    lessonName: lesson?.name || '',
    date: lesson?.date || '',
    time: lesson?.time?.slice(0, 5) || '',
    description: lesson?.description || '',
    street: splitAddress[0] ?? '',
    streetTwo: splitAddress[1] ?? '',
    state: splitAddress[2] ?? '',
    city: splitAddress[3] ?? '',
    zipCode: splitAddress[4] ?? '',
    isDraft: true,
  });
  const [unchanged, setUnchanged] = useState(true);
  const [errors, setErrors] = useState();
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [allDayCheckbox, setAllDayCheckbox] = useState(lesson?.time === null);
  const { t2 } = useTranslation('EditCourse');
  const { openModal, setOpenModal } = useModal();
  const { users, enrolledUsers } = useUsers({ courseId, lessonId: lesson?.id });

  useEffect(() => {
    if (alert) {
      setAlertMessage(t2(`${alert}`));
      setAlertOpen(true);
      setTimeout(() => {
        setAlert('');
      }, 2000);
    }
  }, [alert]);

  const { isDraft, isPrivate, privateUsers, setPrivateUsers, setIsPrivate, setIsDraft, onDelete } = useLesson({
    module,
    updateLesson,
    lesson,
    deleteLesson,
  });

  const { onCreateSession, onUpdateSession } = useSession({
    addLessonSession,
    module,
    updateLesson,
    updateLessonSessionData,
    isDraft,
    isPrivate,
    privateUsers,
    setPrivateUsers,
  });

  const enableButton = () => {
    if (lesson?.id) return t2('SaveChanges');

    return t2('Save');
  };

  const handleModalAccept = async () => {
    setOpenModal(false);

    if (lesson?.id) {
      await onUpdateSession({ ...formData, id: lesson.id });
      return;
    }

    onCreateSession(formData, FACE_TO_FACE);
  };

  const handleModalClose = () => {
    setOpenModal(false);
  };

  const handleChange = event => {
    setUnchanged(false);

    if (event.target.name === 'zipCode') {
      const truncatedValue = event.target.value.slice(0, 5);
      setFormData({
        ...formData,
        [event.target.name]: truncatedValue,
      });

      return;
    }
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleCheckbox = async () => {
    setUnchanged(false);

    await setFormData({
      ...formData,
      time: '00:00',
    });

    setFormData({ ...formData, time: '' });
    setAllDayCheckbox(!allDayCheckbox);
  };

  const validate = () => {
    const temp = {};

    temp.lessonName = formData.lessonName.match(NO_EMPTY_NO_EMPTY_SPACES) ? t2('FormErrorSessionName') : '';

    // Valida si la fecha ingresada ya paso ignorando el tiempo.
    temp.date =
      FormValidations.dayHasPassed(formData.date, {
        message: t2('FormErrorSessionDate'),
      }) || '';

    // Obtenemos la fecha junto con el tiempo.
    const datetime = formData.time ? `${formData.date}T${formData.time}` : undefined;

    // Valida si la fecha ingresada ya paso tomando en cuenta el tiempo.
    temp.time =
      FormValidations.datetimeHasPassed(datetime, {
        message: t2('FormErrorSessionTime'),
      }) || '';

    temp.street = formData.street.match(NO_EMPTY_NO_EMPTY_SPACES) ? t2('FormErrorSessionGeneric') : '';

    if (allDayCheckbox) {
      temp.time = '';
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every(x => x === '');
  };

  const checkValidation = () => {
    const isValid = validate();

    if (isValid) {
      setOpenModal(true);
    }
  };

  const handleUserSelect = array => {
    setUnchanged(false);
    setPrivateUsers([...array]);
  };

  return (
    <div>
      <Title
        name={t2('LessonFace')}
        textButton={enableButton()}
        check={isDraft}
        setCheck={setIsDraft}
        setUnchanged={setUnchanged}
        checkText={t2('Draft')}
        checkInfo={t2('DraftCondition')}
        checkDisabled={false}
        setOpenModal={checkValidation}
        buttonDisabled={unchanged}
      >
        <Options Icon={<MoreVert />} disabledEffect>
          <MenuItem onClick={onDelete}>{t2('DeleteLesson')}</MenuItem>
        </Options>
      </Title>
      <FormContainer>
        <TextInput
          name="lessonName"
          label={t2('LabelSessionName')}
          color="MexlogGreen"
          value={formData?.lessonName}
          onChange={handleChange}
          style={nameStyle}
          errortext={errors?.lessonName}
        />
        <DateContainer>
          <TextInput
            name="date"
            label={t2('LabelSessionDate')}
            color="MexlogGreen"
            value={formData?.date}
            onChange={handleChange}
            style={dateStyle}
            errortext={errors?.date}
            type="date"
          />
          <TextInput
            name="time"
            label={t2('LabelSessionTimeStart')}
            color="MexlogGreen"
            value={formData?.time}
            onChange={handleChange}
            style={timeStyle}
            errortext={errors?.time}
            type="time"
            disabled={allDayCheckbox || formData?.date === ''}
          />
        </DateContainer>
        <DateOptions>
          <FormControlLabel
            name="allDay"
            onChange={handleCheckbox}
            control={<Checkbox defaultChecked={allDayCheckbox} />}
            label="Todo el dia"
          />
        </DateOptions>
        <AddressContainer>
          <AddressLine>
            <TextInput
              name="street"
              label={t2('LabelSessionStreet')}
              color="MexlogGreen"
              value={formData?.street}
              onChange={handleChange}
              style={streetStyle}
              errortext={errors?.street}
            />
            <TextInput
              name="streetTwo"
              label={t2('LabelSessionStreetTwo')}
              color="MexlogGreen"
              value={formData?.streetTwo}
              onChange={handleChange}
              style={streetStyle}
            />
          </AddressLine>
          <AddressLine>
            <TextInput
              name="city"
              label={t2('LabelSessionCity')}
              color="MexlogGreen"
              value={formData?.city}
              onChange={handleChange}
              style={streetStyle}
            />
            <TextInput
              name="state"
              label={t2('LabelSessionState')}
              color="MexlogGreen"
              value={formData?.state}
              onChange={handleChange}
              style={streetStyle}
            />
          </AddressLine>
          <TextInput
            name="zipCode"
            label={t2('LabelSessionZipCode')}
            color="MexlogGreen"
            value={formData?.zipCode}
            onChange={handleChange}
            style={zipCodeStyle}
            type="phone"
          />
        </AddressContainer>
        <IconTextContainer>
          <Notes />
          <TextField
            id="description"
            name="description"
            value={formData?.description || ''}
            placeholder={t2('LabelSessionDescription')}
            multiline
            minRows={3}
            maxRows={4}
            onChange={handleChange}
            sx={{
              width: '100%',
              '& .MuiInputBase-root': {
                backgroundColor: colors.WHITE,
                width: '100%',
              },
            }}
            InputProps={{ disableunderline: 'true' }}
            color="success"
          />
        </IconTextContainer>
        <PrivateLessonForm
          isPrivate={isPrivate}
          setIsPrivate={setIsPrivate}
          users={users}
          privateUsers={privateUsers}
          selectHandler={handleUserSelect}
          enrolledUsers={enrolledUsers}
          setUnchanged={setUnchanged}
        />
      </FormContainer>
      <Modal
        open={openModal}
        setOpen={setOpenModal}
        Children={
          <ModalInformation accept={handleModalAccept} cancel={handleModalClose} title={t2('ModalTitleSession')} />
        }
      />
      <Alert message={alertMessage} open={alertOpen} setOpen={setAlertOpen} />
    </div>
  );
}

export default LessonFace;
